import React, { useEffect, useState, useCallback } from 'react';
import { Table, Spinner, Alert, Form, Row, Col, Button, Pagination } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import useAuthHeaders from '../../hooks/useAuthHeaders';

const API_BASE = process.env.REACT_APP_API_URL || 'https://localhost:5001/api/v1';
const PAGE_SIZE = 50;

const toDateInput = (d) => d.toISOString().slice(0, 10);

const today = new Date();
const defaultDateTo = toDateInput(today);
const defaultDateFrom = toDateInput(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 6));

const OPERATION_OPTIONS = [
  '',
  'descargametadatafiel',
  'descargaretencionmetadatafiel',
  'efos',
  'fac',
  'facfiel',
  'GetCaptcha',
  'GetMetadaatFiel',
  'imssoc',
  'ocfiel',
  'retencionfiel',
  'v1/csf',
  'v1/dec',
  'v1/oc',
  'v2/comunicadosfiel',
  'v2/csf',
  'v2/csffiel',
  'v2/dec',
  'v2/decfiel',
  'v2/fac',
  'v2/informacionfiscal',
  'v2/informacionfiscalfiel',
  'v2/notificacionesfiel',
  'v2/oc',
];

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('es-MX', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
};

const SortIcon = ({ col, sortBy, sortDir }) => {
  if (sortBy !== col) return <span style={{ opacity: 0.3, marginLeft: 4 }}>↕</span>;
  return <span style={{ marginLeft: 4 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
};

const COLUMNS = [
  { key: 'date',           label: 'Fecha' },
  { key: 'email',          label: 'Email' },
  { key: 'rfc',            label: 'RFC' },
  { key: 'operation',      label: 'Operación' },
  { key: 'requestcount',   label: 'Solicitudes' },
  { key: 'facturacount',   label: 'Facturas' },
];

const AdminDailySummaryReport = () => {
  const { getAuthHeaders } = useAuthHeaders();
  const navigate = useNavigate();

  const [dateFromInput, setDateFromInput] = useState(defaultDateFrom);
  const [dateToInput, setDateToInput] = useState(defaultDateTo);
  const [rfcInput, setRfcInput] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [operationInput, setOperationInput] = useState('');

  const [query, setQuery] = useState({
    dateFrom: defaultDateFrom,
    dateTo: defaultDateTo,
    rfc: '',
    email: '',
    operation: '',
    sortBy: 'date',
    sortDir: 'desc',
    page: 1,
    pageSize: PAGE_SIZE,
  });

  const [result, setResult] = useState({ items: [], totalCount: 0, page: 1, pageSize: PAGE_SIZE, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchReport = useCallback(async (q) => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const params = new URLSearchParams();
      if (q.dateFrom) params.set('dateFrom', q.dateFrom);
      if (q.dateTo)   params.set('dateTo', q.dateTo);
      if (q.rfc)       params.set('rfc', q.rfc);
      if (q.email)     params.set('email', q.email);
      if (q.operation) params.set('operation', q.operation);
      params.set('sortBy',   q.sortBy);
      params.set('sortDir',  q.sortDir);
      params.set('page',     q.page);
      params.set('pageSize', q.pageSize);

      const response = await fetch(`${API_BASE}/admin/reports/daily-summary?${params}`, { headers });
      if (!response.ok) throw new Error(`Error ${response.status}`);
      const data = await response.json();
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchReport(query);
  }, [query, fetchReport]);

  const handleSearch = () => {
    setQuery(prev => ({
      ...prev,
      dateFrom: dateFromInput,
      dateTo: dateToInput,
      rfc: rfcInput,
      email: emailInput,
      operation: operationInput,
      page: 1,
    }));
  };

  const handleClear = () => {
    setDateFromInput(defaultDateFrom);
    setDateToInput(defaultDateTo);
    setRfcInput('');
    setEmailInput('');
    setOperationInput('');
    setQuery(prev => ({
      ...prev,
      dateFrom: defaultDateFrom,
      dateTo: defaultDateTo,
      rfc: '',
      email: '',
      operation: '',
      page: 1,
    }));
  };

  const handleSort = (col) => {
    setQuery(prev => ({
      ...prev,
      sortBy: col,
      sortDir: prev.sortBy === col && prev.sortDir === 'asc' ? 'desc' : 'asc',
      page: 1,
    }));
  };

  const handlePage = (p) => setQuery(prev => ({ ...prev, page: p }));

  const totalPages = result.totalPages ?? 0;
  const currentPage = result.page ?? 1;

  const buildPageItems = () => {
    const items = [];
    const delta = 2;
    const left = Math.max(1, currentPage - delta);
    const right = Math.min(totalPages, currentPage + delta);
    if (left > 1) {
      items.push(1);
      if (left > 2) items.push('...');
    }
    for (let i = left; i <= right; i++) items.push(i);
    if (right < totalPages) {
      if (right < totalPages - 1) items.push('...');
      items.push(totalPages);
    }
    return items;
  };

  return (
    <div className="status-container">
      <div className="page-header mb-4">
        <div>
          <Button
            variant="link"
            size="sm"
            className="p-0 mb-1 text-muted"
            onClick={() => navigate('/admin/reports')}
          >
            ← Reportes
          </Button>
          <h2 className="page-title">Resumen diario de uso</h2>
          <p className="page-subtitle text-muted">
            {result.totalCount} registro{result.totalCount !== 1 ? 's' : ''} encontrado{result.totalCount !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-3 p-3 border rounded bg-light">
        <Row className="g-2 align-items-end">
          <Col xs={6} md={2}>
            <Form.Label className="small mb-1">Fecha desde</Form.Label>
            <Form.Control
              size="sm"
              type="date"
              value={dateFromInput}
              onChange={e => setDateFromInput(e.target.value)}
            />
          </Col>
          <Col xs={6} md={2}>
            <Form.Label className="small mb-1">Fecha hasta</Form.Label>
            <Form.Control
              size="sm"
              type="date"
              value={dateToInput}
              onChange={e => setDateToInput(e.target.value)}
            />
          </Col>
          <Col xs={6} md={3}>
            <Form.Label className="small mb-1">RFC</Form.Label>
            <Form.Control
              size="sm"
              placeholder="Filtrar por RFC..."
              value={rfcInput}
              onChange={e => setRfcInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
            />
          </Col>
          <Col xs={6} md={2}>
            <Form.Label className="small mb-1">Email</Form.Label>
            <Form.Control
              size="sm"
              placeholder="Filtrar por email..."
              value={emailInput}
              onChange={e => setEmailInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
            />
          </Col>
          <Col xs={6} md={3}>
            <Form.Label className="small mb-1">Operación</Form.Label>
            <Form.Select
              size="sm"
              value={operationInput}
              onChange={e => setOperationInput(e.target.value)}
            >
              {OPERATION_OPTIONS.map(op => (
                <option key={op} value={op}>{op || 'Todas'}</option>
              ))}
            </Form.Select>
          </Col>
          <Col xs={12} md={1} className="d-flex gap-1">
            <Button size="sm" variant="primary" onClick={handleSearch} className="w-100">
              Buscar
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={handleClear} title="Limpiar">
              ✕
            </Button>
          </Col>
        </Row>
      </div>

      {error && <Alert variant="danger">Error al cargar reporte: {error}</Alert>}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" style={{ color: '#13a0ff' }} />
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <Table hover size="sm" className="mb-2">
              <thead className="table-light">
                <tr>
                  {COLUMNS.map(({ key, label }) => (
                    <th
                      key={key}
                      onClick={() => handleSort(key)}
                      style={{ cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none' }}
                    >
                      {label}
                      <SortIcon col={key} sortBy={query.sortBy} sortDir={query.sortDir} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.items.map((row, i) => (
                  <tr key={i}>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatDate(row.date)}</td>
                    <td>{row.email ?? '—'}</td>
                    <td>{row.rfc ?? '—'}</td>
                    <td>{row.operation ?? '—'}</td>
                    <td className="text-end">{row.requestCount}</td>
                    <td className="text-end">{row.facturaCount}</td>
                  </tr>
                ))}
                {result.items.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center text-muted py-4">
                      No se encontraron registros con los filtros aplicados.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-2">
              <small className="text-muted">
                Página {currentPage} de {totalPages} ({result.totalCount} resultados)
              </small>
              <Pagination size="sm" className="mb-0">
                <Pagination.Prev disabled={currentPage === 1} onClick={() => handlePage(currentPage - 1)} />
                {buildPageItems().map((item, i) =>
                  item === '...'
                    ? <Pagination.Ellipsis key={`e${i}`} disabled />
                    : <Pagination.Item key={item} active={item === currentPage} onClick={() => handlePage(item)}>{item}</Pagination.Item>
                )}
                <Pagination.Next disabled={currentPage === totalPages} onClick={() => handlePage(currentPage + 1)} />
              </Pagination>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminDailySummaryReport;
